import {
  IContractAgreementResponse,
  IContractCustomSectionResponse,
  IContractDistributionResponse,
} from 'services/contract/types';
import { AGREEMENT_TYPE } from 'services/templates/agreement-template/types';
import {
  IContractTemplateTreeItemForm,
  IDistributionType,
  SETTLEMENT_TYPE,
  TEMPLATE_TYPE,
} from 'services/templates/types';
import {
  getDefaultShareFormValue,
  getSharePercentFromResponse,
  inferSettlementTypeFromDistribution,
} from 'utils/components/contract-template/distribution/settlementType.utils';
import { isParagraphCustomSection } from 'utils/components/contract-template/section/customSection.utils';

export const distributionSectionQueryToForm = (
  distributionSection: IContractDistributionResponse,
  templateType: IDistributionType,
  extremeId?: number
): IContractTemplateTreeItemForm => {
  const settlementType = inferSettlementTypeFromDistribution(distributionSection);

  const distributionChildren: IContractTemplateTreeItemForm[] =
    distributionSection.shares.map(
      (
        { salesChannelId, percent, percentage, amountPerUnit, salesChannelName },
        key
      ) => ({
        id: `share-${TEMPLATE_TYPE[templateType]}-${
          extremeId ? extremeId + key + 1 : key + 2
        }`,
        type: TEMPLATE_TYPE.agreement,
        settlementType,
        content: {
          type: AGREEMENT_TYPE.shares,
          distributionTemplateShares: {
            id: salesChannelId,
            name: salesChannelName ?? '',
            ...getDefaultShareFormValue(settlementType, {
              percent: getSharePercentFromResponse({ percent, percentage }),
              amountPerUnit,
            }),
          },
        },
        children: [],
      })
    );

  distributionChildren.unshift({
    id: `default-share-${TEMPLATE_TYPE[templateType]}`,
    type: TEMPLATE_TYPE.agreement,
    settlementType,
    content: {
      type: AGREEMENT_TYPE.defaultShare,
      distributionTemplateShares: {
        id: `default-share-agreement-${TEMPLATE_TYPE[templateType]}`,
        name: '',
        ...getDefaultShareFormValue(settlementType, {
          percent: distributionSection.defaultSharePercentage,
          amountPerUnit: distributionSection.defaultAmountPerUnit,
        }),
      },
    },
    children: [],
    disabledSelection: true,
  });

  const distribution: IContractTemplateTreeItemForm = {
    id: `distribution-${TEMPLATE_TYPE[templateType]}`,
    type: templateType,
    content: distributionSection.title,
    settlementType,
    children: distributionChildren,
  };

  return distribution;
};

interface IConnectContractTreeItems {
  customSections?: IContractCustomSectionResponse[];
  digitalDistribution?: IContractDistributionResponse;
  physicalDistribution?: IContractDistributionResponse;
  merchDistribution?: IContractDistributionResponse;
  channelDistribution?: IContractDistributionResponse;
  agreements?: IContractAgreementResponse[];
  isParagraphContext?: boolean;
}

export const connectContractTreeItems = (
  contractItems: IConnectContractTreeItems,
  lowestAvailableId?: string
): IContractTemplateTreeItemForm[] => {
  const {
    agreements,
    customSections,
    digitalDistribution,
    physicalDistribution,
    channelDistribution,
    merchDistribution,
    isParagraphContext = false,
  } = contractItems;

  const treeItems: IContractTemplateTreeItemForm[] = [];

  let lowestId = lowestAvailableId ? parseInt(lowestAvailableId) + 1 : 0;

  if (agreements?.length) {
    for (const agreement of agreements) {
      const { position, value, title } = agreement;
      treeItems[position] = {
        id: `${lowestId}`,
        type: TEMPLATE_TYPE.agreement,
        children: [],
        content: isParagraphContext
          ? {
              type: AGREEMENT_TYPE.paragraph,
              selectAgreementTemplate: null,
              textAgreementTemplate: null,
              paragraphAgreementTemplate: { defaultValue: value, title },
            }
          : {
              type: AGREEMENT_TYPE.text,
              selectAgreementTemplate: null,
              textAgreementTemplate: { defaultValue: value, title },
            },
      };

      lowestId += 1;
    }
  }

  if (digitalDistribution && !!digitalDistribution.title.length) {
    const digitalDistributionItem = distributionSectionQueryToForm(
      digitalDistribution,
      TEMPLATE_TYPE.digitalDistribution
    );
    treeItems[digitalDistribution.position] = digitalDistributionItem;
    lowestId += digitalDistributionItem.children.length + 1;
  }

  if (physicalDistribution && !!physicalDistribution.title.length) {
    const physicalDistributionItem = distributionSectionQueryToForm(
      physicalDistribution,
      TEMPLATE_TYPE.physicalDistribution
    );
    treeItems[physicalDistribution.position] = physicalDistributionItem;
    lowestId += physicalDistributionItem.children.length + 1;
  }
  if (merchDistribution && !!merchDistribution.title.length) {
    const merchDistributionItem = distributionSectionQueryToForm(
      merchDistribution,
      TEMPLATE_TYPE.merchDistribution
    );
    treeItems[merchDistribution.position] = merchDistributionItem;
    lowestId += merchDistributionItem.children.length + 1;
  }
  if (channelDistribution && !!channelDistribution.title.length) {
    const channelDistributionItem = distributionSectionQueryToForm(
      channelDistribution,
      TEMPLATE_TYPE.channelDistribution
    );
    treeItems[channelDistribution.position] = channelDistributionItem;
    lowestId += channelDistributionItem.children.length + 1;
  }

  if (customSections?.length) {
    for (const section of customSections) {
      const isParagraphSection = isParagraphCustomSection(section.type);
      const children = connectContractTreeItems(
        {
          agreements: section.agreement,
          customSections: section.customSections,
          isParagraphContext: isParagraphSection,
        },
        `${lowestId + 1}`
      );

      treeItems[section.position] = {
        id: `${lowestId}`,
        type: isParagraphSection
          ? TEMPLATE_TYPE.paragraphSection
          : TEMPLATE_TYPE.section,
        content: section.title,
        children: children,
      };

      lowestId += children.length + 2;
    }
  }

  return treeItems;
};
